import React, { useState } from 'react';
import { User, useUsers } from '../../hooks/useUsers';
import { DebounceInput } from 'react-debounce-input';
import { normalizeMobilePhone } from '../../components/common/phone-validation';
import { convertObjectToParams } from '../../components/common/create-params';
import { useHistory } from 'react-router';
import { ROUTE_PATH } from '../../routes/paths';
import Header from '../../components/common/global-top-bar';
import EditUserDetails from './components/user-edit';
import { Spinner } from '../../components/common';
import { XCricle } from '../../assets/images';
import RightArrow from '../../assets/images/left-arrow-symbol.svg';

export const Users: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const { data: users, isLoading, refetch } = useUsers({ page, search });
  const history = useHistory();

  const handleCancelSearch = () => {
    setSearch('');
    setPage(1);
    setTimeout(() => {
      refetch();
    }, 300);
  };

  const handleSearch = (event: { target: { value: React.SetStateAction<string> } }) => {
    setSearch(event.target.value);
    setPage(1);
    refetch();
  };

  const handleUserRedirect = (user: User) => {
    if (user.caller_role !== null) {
      history.push(
        ROUTE_PATH.MEMBERS_PROFILE +
          convertObjectToParams({
            name: user.first_name,
            id: user.id,
            callerRoleId: user.caller_role_id,
          }),
      );
    } else if (user.listener_role !== null) {
      history.push(
        ROUTE_PATH.LISTENERS_PROFILE +
          convertObjectToParams({
            listenerRoleId: user.listener_role_id,
            listenerId: user.id,
          }),
      );
    }
  };

  return (
    <div>
      {isLoading && <Spinner />}
      <Header heading={'Users'} />
      <div className="gray-border-line bg-gray-background-light flex h-10 w-full items-center justify-between border-b px-7">
        <div className="w-2/4">
          <div className="h-8 w-full">
            <div className="flex h-full w-full rounded-full bg-white">
              <DebounceInput
                minLength={2}
                className="h-full w-5/6 rounded-l-full text-center focus:outline-none"
                placeholder="SEARCH"
                debounceTimeout={300}
                onChange={handleSearch}
                value={search}
              />
              <div
                className="flex h-full w-1/6 items-center justify-center rounded-r-full pr-2"
                onClick={() => {
                  handleCancelSearch();
                }}
              >
                {search.length > 0 && <img src={XCricle} alt="closeButton"></img>}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full">
          <div className="relative mx-7 flex items-center justify-between py-3">
            <div className="flex items-center justify-between px-7 py-3">
              <div className="flex items-center">
                {page > 1 ? (
                  <button
                    className="blue-primary h-6 w-6 rounded-full"
                    onClick={() => {
                      setPage(page - 1);
                    }}
                  >
                    <img className="h-6 w-6" src={RightArrow} alt="left Arrow"></img>
                  </button>
                ) : (
                  <button className="blue-primary h-6 w-6 rounded-full" disabled></button>
                )}
              </div>
              {search === '' ? (
                <div className="relative flex w-full justify-evenly space-x-2 p-2">
                  {page > 1 && page <= 5 ? (
                    <div
                      role="button"
                      className="text-gray-dark"
                      onClick={() => {
                        setPage(page - 1);
                      }}
                    >
                      {page - 1}
                    </div>
                  ) : page > 5 ? (
                    <div
                      role="button"
                      className="text-gray-dark"
                      onClick={() => {
                        setPage(page - 5);
                      }}
                    >
                      {page - 5}...
                    </div>
                  ) : (
                    <div></div>
                  )}
                  <div className="blue-primary font-bold">{page}</div>
                  <div
                    role="button"
                    className="text-gray-dark"
                    onClick={() => {
                      setPage(page + 1);
                    }}
                  >
                    {page + 1}
                  </div>
                  <div
                    role="button"
                    className="text-gray-dark"
                    onClick={() => {
                      setPage(page + 2);
                    }}
                  >
                    {page + 2}
                  </div>
                  <div
                    role="button"
                    className="text-gray-dark"
                    onClick={() => {
                      setPage(page + 3);
                    }}
                  >
                    {page + 3}
                  </div>
                  <div
                    role="button"
                    className="text-gray-dark"
                    onClick={() => {
                      setPage(page + 4);
                    }}
                  >
                    {page + 4}
                  </div>
                </div>
              ) : (
                <div className="relative flex w-full justify-evenly space-x-2 p-2">
                  <div className="blue-primary font-bold">{page}</div>
                </div>
              )}
              <div className="flex items-center">
                {users?.length === 10 ? (
                  <button
                    className="blue-primary h-6 w-6 rotate-180 transform rounded-full"
                    onClick={() => {
                      setPage(page + 1);
                    }}
                  >
                    <img className="h-6 w-6" src={RightArrow} alt="left Arrow"></img>
                  </button>
                ) : (
                  <button className="blue-primary h-6 w-6 rotate-180 transform rounded-full" disabled></button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-window-height overflow-y-auto px-7">
        <table className="table-row-format relative w-full table-fixed border-collapse">
          <thead className="h-full bg-white">
            <tr className="w-full px-7 text-left">
              <th className="sticky top-0 mb-4 h-full w-3/12 bg-white px-2 py-1">Users</th>
              <th className="sticky top-0 mb-4 h-full w-1/12 bg-white px-2 py-2">User ID</th>
              <th className="sticky top-0 mb-4 h-full w-2/12 bg-white px-2 py-2">
                <p className="-pl-4">Role</p>
              </th>
              <th className="sticky top-0 mb-4 h-full w-1/12 bg-white px-2 py-2">
                <p className="-pl-4"></p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="h-2"></tr>
            {users?.map((user: User) => {
              return (
                <tr
                  key={user.id}
                  role="button"
                  className="text-gray-dark h-24 space-y-6 rounded border-4 border-y border-white bg-gray-200 text-left"
                  onClick={() => {
                    handleUserRedirect(user);
                  }}
                >
                  <td className="w-3/12">
                    <div className="flex h-full w-full items-center">
                      <div className="text-gray-dark truncate px-5 py-1">
                        <p>
                          <span className="font-extrabold">
                            {user.first_name} {user.last_name}
                          </span>{' '}
                          {user.display_name ? `(${user.display_name})` : ''}
                        </p>
                        {user.email_address ? <p className="font-bold"> {user.email_address}</p> : ''}
                        {user.mobile_phone ? (
                          <p className="font-bold"> {normalizeMobilePhone(user.mobile_phone.replace('1', ''))}</p>
                        ) : (
                          ''
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="w-1/12 px-2 py-1">{`#${user?.id}`}</td>
                  <td className="w-1/12 truncate py-1 pl-1">
                    <div className="w-2/3">
                      {user.administrator_role !== null ? (
                        <p className="stacked-bar-red-primary my-1 w-full rounded-full px-2 py-1 text-center text-sm text-white">
                          Admin
                        </p>
                      ) : (
                        ''
                      )}
                      {user.caller_role !== null ? (
                        <p className="stacked-bar-green-primary my-1 w-full rounded-full px-2 py-1 text-center text-sm text-white">
                          Caller
                        </p>
                      ) : (
                        ''
                      )}
                      {user.client_administrator_role !== null ? (
                        <p className="stacked-bar-orange-primary my-1 w-full rounded-full px-2 py-1 text-center text-sm text-white">
                          Client Admin
                        </p>
                      ) : (
                        ''
                      )}
                      {user.listener_role !== null ? (
                        <p className="stacked-bar-blue-100 my-1 w-full rounded-full px-2 py-1 text-center text-sm text-white">
                          Listener
                        </p>
                      ) : (
                        ''
                      )}
                    </div>
                  </td>
                  <td>
                    <EditUserDetails key={user.id} user={user} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
